import React, { useEffect, useMemo, useState } from "react";

import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { setQueuedScanLabelNames } from "actions/queuedScanLabels";

import { OutlineButton } from "components/Button";
import { StyledInput } from "components/Form/FormikControls";
import { Column, Row } from "components/Layout";

import { ScanLabelOptionRow, ScanLabelOptionData } from "./ScanLabelOptionRow";

import {
  ScanLabelArchetype,
  SubTextByScanLabelArchetype,
} from "constants/scanLabels";

import { getQueuedScanLabelNames, getScanLabels } from "selectors";

const PickerBody = styled(Column)(
  ({ theme }) => `
  width: 100%;
  max-width: min(320px, calc(100vw - ${theme.space[4]}px));
  padding: ${theme.space[2]}px;
`,
);

const EqualWidthButtonRow = styled(Row)(
  ({ theme }) => `
  gap: ${theme.space[1]}px;

  > * {
    flex: 1;
  }
`,
);

interface ScanLabelsPopoverProps {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  closeSelf: () => void;
}

export const ScanLabelsPopover: React.FC<ScanLabelsPopoverProps> = ({
  anchorEl,
  isOpen,
  closeSelf,
}) => {
  const dispatch = useDispatch();

  const scanLabelsById = useSelector(getScanLabels);
  const queuedScanLabelNames = useSelector(getQueuedScanLabelNames);

  const [selectedNames, setSelectedNames] =
    useState<string[]>(queuedScanLabelNames);
  const [customLabelNames, setCustomLabelNames] = useState<string[]>([]);
  const [customLabelInput, setCustomLabelInput] = useState("");
  const [expandedNames, setExpandedNames] = useState<string[]>([]);

  // The popover stays mounted for the whole scanning session (only its
  // `open` prop toggles), but the queue in the store can change or clear
  // between opens (e.g. after an "Add to X" apply). Re-seed local state
  // from the store each time it opens, instead of only at mount.
  useEffect(() => {
    if (isOpen) {
      setSelectedNames(queuedScanLabelNames);
      setCustomLabelNames([]);
      setExpandedNames([]);
      setCustomLabelInput("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const options: ScanLabelOptionData[] = useMemo(() => {
    const optionsByName = new Map<string, ScanLabelOptionData>();

    Object.values(ScanLabelArchetype)
      .filter(archetype => archetype !== ScanLabelArchetype.NONE)
      .forEach(archetype =>
        optionsByName.set(archetype, {
          name: archetype,
          archetype,
          subtext: SubTextByScanLabelArchetype[archetype],
        }),
      );

    Object.values(scanLabelsById).forEach(scanLabel => {
      if (!optionsByName.has(scanLabel.name)) {
        optionsByName.set(scanLabel.name, {
          name: scanLabel.name,
          archetype: scanLabel.archetype,
        });
      }
    });

    customLabelNames.forEach(customLabelName => {
      if (!optionsByName.has(customLabelName)) {
        optionsByName.set(customLabelName, {
          name: customLabelName,
          archetype: ScanLabelArchetype.NONE,
        });
      }
    });

    return Array.from(optionsByName.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [scanLabelsById, customLabelNames]);

  function toggleExpanded(name: string) {
    setExpandedNames(previous =>
      previous.includes(name)
        ? previous.filter(expandedName => expandedName !== name)
        : [...previous, name],
    );
  }

  function toggleOption(name: string) {
    setSelectedNames(previous =>
      previous.includes(name)
        ? previous.filter(selectedName => selectedName !== name)
        : [...previous, name],
    );
  }

  function onAddCustomLabel() {
    const name = customLabelInput.trim();
    if (!name) {
      return;
    }
    setCustomLabelNames(previous =>
      previous.includes(name) ? previous : [...previous, name],
    );
    setSelectedNames(previous =>
      previous.includes(name) ? previous : [...previous, name],
    );
    setCustomLabelInput("");
  }

  function onConfirmSelection() {
    dispatch(setQueuedScanLabelNames(selectedNames));
    closeSelf();
  }

  return (
    <Popover
      open={isOpen}
      onClose={closeSelf}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <PickerBody>
        {options.map(option => (
          <ScanLabelOptionRow
            key={option.name}
            option={option}
            isSelected={selectedNames.includes(option.name)}
            isExpanded={expandedNames.includes(option.name)}
            onToggleSelect={toggleOption}
            onToggleExpand={toggleExpanded}
          />
        ))}

        <Row alignCenter gridGap={1} marginTop={1}>
          <StyledInput
            value={customLabelInput}
            onChange={e => setCustomLabelInput(e.target.value)}
            placeholder="Add a custom label"
            autoComplete="off"
            maxLength={50}
          />
          <OutlineButton
            color="primary"
            onClick={onAddCustomLabel}
            disabled={!customLabelInput.trim()}
          >
            <FontAwesomeIcon icon={faPlus} />
          </OutlineButton>
        </Row>

        <EqualWidthButtonRow marginTop={2}>
          <OutlineButton color="primary" onClick={closeSelf}>
            Cancel
          </OutlineButton>
          <OutlineButton color="primary" onClick={onConfirmSelection}>
            Confirm Selection
          </OutlineButton>
        </EqualWidthButtonRow>
      </PickerBody>
    </Popover>
  );
};
